import React from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { get } from 'lodash';

import CardListItem from '@dashboard/components/CardListItem';
import TableRowElement from '@dashboard/components/TableRowElement';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import PaymentStatusBadge from './PaymentStatusBadge';
import isRecoveredAbandonment from '@/dashboard/services/abandonment/isRecoveredAbandonment';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';
import Tooltip from '../Tooltip';
import { formatDateTime } from '@/dashboard/lib/format-date';
import StatusBadge from '../StatusBadge';
import Dash from '../Dash';

const propTypes = {
  payment: PropTypes.object.isRequired,
};

const PaymentItem = ({ payment, options = {} }) => {
  const isCompact = options.appearance === 'compact';
  const isBooking = payment.tickets && payment.tickets.length > 0;

  return (
    <CardListItem
      href={isBooking ? `/orders/bookings/${payment._id}` : `/orders/payments/${payment._id}`}
    >
      {!options.hideCustomer && (
        <TableRowElement size={isCompact ? 'xl' : '3xl'}>
          <div className="font-medium">
            {(payment.customer && get(payment, 'customer.email')) ||
              get(payment, 'customerEmail') || <Dash />}
          </div>
          <div className="text-sm text-gray-500">
            {payment.customer && get(payment, 'customer.name')}
          </div>
        </TableRowElement>
      )}

      <TableRowElement size={isCompact ? 'small' : 'large'} className="font-medium">
        {getPriceLabel({
          amount: payment.amount,
          currency: payment.currency,
        })}
      </TableRowElement>

      {!options.hidePaymentLink && (
        <TableRowElement size={isCompact ? 'xl' : '4xl'}>
          {get(payment, 'page.name') || <Dash />}
        </TableRowElement>
      )}

      <TableRowElement size="xl" align="right">
        {format(new Date(get(payment, 'createdAt')), 'd MMM, yyyy, HH:mm')}
      </TableRowElement>

      <TableRowElement size="large">
        {(() => {
          const isRecovered = isRecoveredAbandonment(payment);
          const isAbandoned = isAbandonedAbandonment(payment);

          if (isAbandoned) {
            return <PaymentStatusBadge status="abandoned" livemode={payment.livemode} />;
          }

          if (isRecovered) {
            return (
              <Tooltip label={`Recovered on ${formatDateTime(payment.recoveredAt)}`}>
                <StatusBadge status="recovered" label="Recovered" withInfoIcon />
              </Tooltip>
            );
          }

          return <>-</>;
        })()}
      </TableRowElement>

      {!options.hideStatus && (
        <>
          {(() => {
            const isCanceledOrArchived =
              payment.orderStatus === 'canceled' || payment.orderStatus === 'archived';
            const isRefunded = payment.status === 'refunded' || payment.status === 'partial_refund';

            /** Show both badges when canceled + refunded */
            if (payment.orderStatus === 'canceled' && isRefunded) {
              return (
                <TableRowElement size="medium" className="gap-1">
                  <PaymentStatusBadge status={payment.orderStatus} livemode={payment.livemode} />
                  <PaymentStatusBadge
                    status={payment.status}
                    livemode={payment.livemode}
                    error={payment.paymentError}
                  />
                </TableRowElement>
              );
            }

            if (isCanceledOrArchived && !isRefunded) {
              return (
                <TableRowElement size="medium">
                  <PaymentStatusBadge status={payment.orderStatus} livemode={payment.livemode} />
                </TableRowElement>
              );
            }

            return (
              <TableRowElement size="medium">
                <PaymentStatusBadge
                  status={payment.status}
                  livemode={payment.livemode}
                  error={payment.paymentError}
                />
              </TableRowElement>
            );
          })()}
        </>
      )}
    </CardListItem>
  );
};

PaymentItem.propTypes = propTypes;

export default PaymentItem;
